import React, { useState, useEffect } from 'react';
import { usePage } from '@inertiajs/react';
import { PageProps } from '@inertiajs/core';
import { useForm } from '@mantine/form';
import {
  Container,
  TextInput,
  MultiSelect,
  Button,
  MantineProvider,
  createTheme,
  Box,
  Paper,
  Title,
  Text,
  Group,
} from '@mantine/core';
import { Notifications, showNotification } from '@mantine/notifications';
import { router } from '@inertiajs/react';
import { IconAlertCircle, IconCheck, IconLink } from '@tabler/icons-react';
import { RichTextEditor } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import Highlight from '@tiptap/extension-highlight';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Heading from '@tiptap/extension-heading';
import Link from '@tiptap/extension-link';
import ImageUploadModal from '../../components/ImageUploadModal';
import { useUser } from '../../src/context/UserContext';

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '../../styles/Login-form.css';
import '@mantine/tiptap/styles.css';

interface Artist {
  id: number;
  name: string;
  avatar_url?: string;
}

interface Organizer {
  id: number;
  name: string;
  slug: string;
  description: string;
  website_url?: string;
  instagram_url?: string;
  facebook_url?: string;
  organization_image_url?: string;
  organizer_status_id: number;
  artists: Artist[];
  user_ids?: number[];
  [key: string]: any;
}

interface EditOrganizerPageProps extends PageProps {
  organizer: Organizer;
  artists: Artist[];
  errors?: Record<string, string[]>;
  all_users: Array<{ id: number; username: string }>;
  org_users: Array<{ id: number; username: string }>;
}

interface User {
  id: number;
  roles?: string[];
  [key: string]: any;
}

const theme = createTheme({
  primaryColor: 'orange',
  colors: {
    orange: [
      '#FEF5ED',
      '#FDE3D1',
      '#FCB693',
      '#FA9D73',
      '#F87F4D',
      '#F66A33',
      '#EA580C',
      '#C44B0A',
      '#9E3D08',
      '#783006',
    ],
  },
  fontFamily: 'Poppins, sans-serif',
  headings: { fontFamily: 'Poppins, sans-serif' },
});

export default function EditOrganizer() {
  const { organizer, artists, all_users, org_users, errors } = usePage<EditOrganizerPageProps>().props;
  const [logoImagePreview, setLogoImagePreview] = useState<string | null>(organizer.organization_image_url);
  const { user } = useUser() as { user: User | null; refreshUser: () => Promise<void> };

  const orgUsers = org_users.map((user) => ({
    value: user.id.toString(),
    label: user.username,
  }));

  const allUsers = all_users.map((user) => ({
    value: user.id.toString(),
    label: user.username,
  }));

  const initialUserIds = orgUsers.map((opt) => Number(opt.value));

  // console.log('🚀 Users:', orgUsers);
  // console.log('🚀 All Users:', allUsers);

  const isSuperAdmin = user?.roles?.includes('SuperAdmin') || false;

  // Check permissions for editing
  const isAuthorized =
    user &&
    (user.roles?.includes('SuperAdmin') || user.roles?.includes('Editor') || organizer.user_ids?.includes(user.id));

  // Redirect if not authorized
  useEffect(() => {
    if (!isAuthorized) {
      window.location.href = `/o/${organizer.slug}`;
    }
  }, [isAuthorized, organizer.slug]);

  // console.log('🔍 Debug organizer data:', organizer);
  // console.log('🔍 Artists data:', organizer.artists);
  // console.log('🔍 All props:', { organizer, artists, all_users, org_users });

  const form = useForm({
    initialValues: {
      name: organizer.name,
      slug: organizer.slug,
      description: organizer.description,
      website_url: organizer.website_url || '',
      facebook_url: organizer.facebook_url || '',
      instagram_url: organizer.instagram_url || '',
      artistIds: organizer.artists.map((artist) => artist.id) || [],
      organizationUserIds: initialUserIds,
      organization_image: null as File | null,
    },
    validate: {
      name: (value) => (value ? null : 'Name is required'),
      slug: (value) => (value ? null : 'Slug is required'),
      description: (value) => (value ? null : 'Description is required'),
      website_url: (value) => {
        if (!value) return null;
        return value.startsWith('http://') || value.startsWith('https://')
          ? null
          : 'URL must start with http:// or https://';
      },
      facebook_url: (value) => {
        if (!value) return null;
        return value.startsWith('http://') || value.startsWith('https://')
          ? null
          : 'URL must start with http:// or https://';
      },
      instagram_url: (value) => {
        if (!value) return null;
        return value.startsWith('http://') || value.startsWith('https://')
          ? null
          : 'URL must start with http:// or https://';
      },
    },
  });

  console.log('🔍 Debug artists data:', artists);
  console.log('🔍 Selected artist IDs:', form.values.artistIds);
  console.log(
    '🔍 Found artists with avatars:',
    form.values.artistIds
      .map((id) => {
        const artist = artists.find((a) => a.id === id);
        return artist
          ? {
              id: artist.id,
              name: artist.name,
              avatar_url: artist.avatar_url,
            }
          : null;
      })
      .filter(Boolean),
  );

  const handleImageChange = (file: File) => {
    // Update the form state with the new file
    form.setFieldValue('organization_image', file);
    setLogoImagePreview(URL.createObjectURL(file));
    // Update the preview with a new URL
    console.log('Image changed - file stored in form:', file.name);
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      Highlight,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Heading.configure({ levels: [1, 2, 3] }),
      Link.configure({ openOnClick: true }),
    ],
    content: organizer.description,
    onUpdate: ({ editor }) => {
      form.setFieldValue('description', editor.getHTML());
    },
  });

  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (values: typeof form.values) => {
    // console.log('📍 Submitting form with values:', values);
    setIsLoading(true);
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;

    try {
      const formData = new FormData();

      // Basic organizer fields
      formData.append('organizer[name]', values.name);
      formData.append('organizer[slug]', values.slug);
      formData.append('organizer[description]', values.description);
      formData.append('organizer[website_url]', values.website_url || '');
      formData.append('organizer[facebook_url]', values.facebook_url || '');
      formData.append('organizer[instagram_url]', values.instagram_url || '');

      // Append artist IDs
      if (values.artistIds.length > 0) {
        values.artistIds.forEach((artistId) => {
          formData.append('organizer[artist_ids][]', artistId.toString());
        });
      }

      // Append organization user IDs (only for SuperAdmins)
      if (isSuperAdmin && values.organizationUserIds.length > 0) {
        values.organizationUserIds.forEach((userId) => {
          formData.append('organizer[user_ids][]', userId.toString());
        });
      }

      // Append organization image if present
      const logoFile = form.values.organization_image;
      if (logoFile instanceof File) {
        formData.append('organizer[organization_image]', logoFile);
      }

      // Send the PUT request
      const response = await fetch(`/o/${organizer.slug}`, {
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken || '',
        },
        credentials: 'include',
        body: formData,
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        showNotification({
          color: 'green',
          title: 'Update Successful',
          message: 'Organizer was successfully updated!',
          icon: <IconCheck />,
          autoClose: 6000,
          position: 'top-right',
        });

        setTimeout(() => {
          const redirectSlug = data.slug || values.slug;
          window.location.href = `/o/${redirectSlug}`;
        }, 1500);
      } else {
        showNotification({
          color: 'red',
          title: 'Update Failed',
          message: data.error || 'Something went wrong!',
          icon: <IconAlertCircle />,
          autoClose: 5000,
        });
        if (data.errors) form.setErrors(data.errors);
      }
    } catch (error) {
      showNotification({
        color: 'red',
        title: 'Error',
        message: 'An unexpected error occurred',
        icon: <IconAlertCircle />,
        autoClose: 5000,
      });
      console.error('Error updating organizer:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to generate a slug from the name
  const generateSlugFromName = (name: string) => {
    if (!name) return '';
    return name
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with a single hyphen
      .trim();
  };

  // Set slug automatically when name changes
  useEffect(() => {
    if (form.values.name) {
      form.setFieldValue('slug', generateSlugFromName(form.values.name));
    }
  }, [form.values.name]);

  if (!isAuthorized) {
    return null; // Don't render anything if not authorized
  }

  return (
    <MantineProvider theme={theme}>
      <Notifications />
      <Container size="sm" my="3rem" style={{ minHeight: '75vh' }}>
        <Paper p="lg" shadow="xs" style={{ borderRadius: '8px', backgroundColor: '#3d3c3e' }} pos="relative">
          <Title order={3} style={{ color: '#FFF7ED' }} mb="sm">
            Edit Organizer: {organizer.name}
          </Title>
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <ImageUploadModal previewUrl={logoImagePreview} onImageChange={handleImageChange} />

            <TextInput
              label="Organizer Name"
              placeholder="Enter the name of the organization"
              {...form.getInputProps('name')}
              mt="md"
              styles={{ label: { color: '#FFF7ED' } }}
              required
            />

            <TextInput
              label="Slug"
              placeholder="your-organization-link"
              description="This is what will appear at the end of your link (wherecanwedance.com/your-organizer-link)"
              {...form.getInputProps('slug')}
              mt="md"
              mb="xl"
              styles={{
                label: { color: '#FFF7ED' },
                description: { color: '#d4d0c8' },
              }}
              required
            />

            <Text size="sm" fw={500} style={{ color: '#FFF7ED' }} mt="lg">
              Organization Description
            </Text>
            {editor && (
              <RichTextEditor editor={editor}>
                <RichTextEditor.Toolbar>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Bold />
                    <RichTextEditor.Italic />
                    <RichTextEditor.Underline />
                    <RichTextEditor.Strikethrough />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Code />
                    <RichTextEditor.Highlight />
                    <RichTextEditor.ClearFormatting />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Link />
                    <RichTextEditor.Unlink />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.H1 />
                    <RichTextEditor.H2 />
                    <RichTextEditor.H3 />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Blockquote />
                    <RichTextEditor.Hr />
                    <RichTextEditor.BulletList />
                    <RichTextEditor.OrderedList />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Undo />
                    <RichTextEditor.Redo />
                  </RichTextEditor.ControlsGroup>
                </RichTextEditor.Toolbar>
                <RichTextEditor.Content />
              </RichTextEditor>
            )}

            <Box mt="xl">
              <MultiSelect
                label="Select Artists"
                name="artistIds"
                hidePickedOptions
                placeholder={form.values.artistIds.length > 0 ? undefined : 'Choose artists'}
                styles={{
                  label: { color: '#FFF7ED', fontSize: '16px' },
                  input: { fontSize: '16px' },
                  dropdown: { fontSize: '16px' },
                }}
                data={artists.map((artist) => ({
                  value: artist.id.toString(),
                  label: artist.name,
                }))}
                value={(form.values.artistIds || []).map(String)}
                onChange={(values) => {
                  const numericValues = Array.isArray(values) ? values.map(Number) : [];
                  form.setFieldValue('artistIds', numericValues);
                }}
                searchable
                clearable
              />

              {/* Display selected artist avatars below */}
              {form.values.artistIds.length > 0 && (
                <Group mt="md" style={{ gap: '4px', flexWrap: 'wrap' }}>
                  {form.values.artistIds.map((id) => {
                    const artist = artists.find((a) => a.id === id);
                    return artist ? (
                      <div
                        key={id}
                        style={{
                          width: '60px',
                          height: '60px',
                          borderRadius: '50%',
                          overflow: 'hidden',
                          boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.2)',
                          border: '2px solid white',
                          position: 'relative',
                        }}
                      >
                        <img
                          src={artist.avatar_url || 'https://placehold.co/200x200?text=Artist'}
                          alt={artist.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      </div>
                    ) : null;
                  })}
                </Group>
              )}
            </Box>

            {isSuperAdmin && (
              <Box mt="xl">
                <MultiSelect
                  label="Organization Users"
                  placeholder={form.values.organizationUserIds.length > 0 ? undefined : 'Search & select users'}
                  data={allUsers}
                  searchable
                  clearable
                  styles={{
                    label: { color: '#FFF7ED', fontSize: '16px' },
                    input: { fontSize: '16px' },
                    dropdown: { fontSize: '16px' },
                  }}
                  value={form.values.organizationUserIds.map(String)}
                  onChange={(vals) =>
                    form.setFieldValue('organizationUserIds', Array.isArray(vals) ? vals.map(Number) : [])
                  }
                />
              </Box>
            )}

            <Group grow mt="xl">
              <TextInput
                label="Website URL"
                placeholder="https://www.example.com"
                {...form.getInputProps('website_url')}
                styles={{ label: { color: '#FFF7ED' } }}
                leftSection={<IconLink size="1rem" />}
              />
            </Group>

            <Group grow mt="md">
              <TextInput
                label="Facebook URL"
                placeholder="https://www.facebook.com/yourpage"
                {...form.getInputProps('facebook_url')}
                styles={{ label: { color: '#FFF7ED' } }}
                leftSection={<IconLink size="1rem" />}
              />
            </Group>

            <Group grow mt="md">
              <TextInput
                label="Instagram URL"
                placeholder="https://www.instagram.com/yourhandle"
                {...form.getInputProps('instagram_url')}
                styles={{ label: { color: '#FFF7ED' } }}
                leftSection={<IconLink size="1rem" />}
              />
            </Group>

            <Button type="submit" mt="xl" size="lg" fullWidth className="mt-5" loading={isLoading} color="#EA580C">
              Update Organizer
            </Button>
          </form>
        </Paper>
      </Container>
    </MantineProvider>
  );
}
